// This token format must stay byte-identical with the server-side regexes that
// resolve locked colours from the same key: map-svc
// (MapController.OverrideColorWithLockedFocusColor) and chart-svc
// (ChartController.TryResolveLockedColor). Changing it here requires a
// coordinated change in both services. A fourth implementation exists in
// eva-map-svc-rs (wells_on_map.rs, override_color_with_locked_focus_color) that
// looks up locks by the bare group title with no "{field}:" prefix, so it
// matches neither key shape; it is not on the live wells-on-map path today
// (src/api/map.ts posts to the .NET map-svc) and would need its own update
// before that path flips to /map-rs.
//
// Intentional: the token carries no quantile type or bin count, so "(Q1)" from
// quartile grouping and "(Q1)" from 10-quantile or IQR grouping resolve to the
// same lock. Locking "the first bin" is treated as a property of the bin's
// position, not of the grouping mode that produced it, so the colour follows
// the user across a grouping change rather than silently reverting.
const QUANTILE_ORDINAL_REGEX = /\((Q\d+|IQR)\)\s*$/;

/** Returns the stable quantile ordinal token (e.g. "(Q1)"/"(IQR)") when the
 *  value ends in one, else the value unchanged. Used so locked colours match
 *  by quantile position instead of the data-derived range, which drifts when
 *  the well set changes. */
export const extractStableLockValue = (value: string): string => {
  // The key this replaced was a template literal, which coerces anything. This runs during
  // module evaluation on JSON.parse'd sessionStorage (appSlice's initialState), so a single
  // non-string value reaching `.match` would throw at import time and blank the whole app —
  // and, since sessionStorage is never rewritten, on every reload after that too.
  if (typeof value !== "string") return value;
  if (!value) return value;
  const match = value.match(QUANTILE_ORDINAL_REGEX);
  return match ? match[0].trim() : value;
};

export const buildColorLockKey = (property: string, value: string): string =>
  `${property}:${extractStableLockValue(value)}`;

/**
 * Adds the legacy full-value key alongside the ordinal key for every entry
 * whose value was shortened to an ordinal token, for the request payload only.
 *
 * The store is keyed on the ordinal alone, so without this a not-yet-upgraded
 * map-svc/chart-svc — which looks the lock up by the full bin title — would
 * miss on *every* quantile lock, including the ones that work today. Emitting
 * both keys makes the rollout order of the three services irrelevant: an
 * upgraded backend reads the ordinal key first, an un-upgraded one finds the
 * full-value key and behaves exactly as it does now.
 *
 * Transitional: drop this once the map-svc and chart-svc ordinal matching is
 * deployed to every environment; the outbound payload is asserted in
 * useChartDataController.test.ts so removing it fails a test rather than
 * silently reverting locks.
 */
export const withLegacyLockKeys = <T extends { property: string; value: string }>(
  lockedColors: Record<string, T>
): Record<string, T> => {
  const withFallbacks: Record<string, T> = { ...lockedColors };
  Object.values(lockedColors).forEach((entry) => {
    if (typeof entry?.property !== "string" || typeof entry?.value !== "string") return;
    const legacyKey = `${entry.property}:${entry.value}`;
    // Belt-and-braces: a legacy key must never shadow an ordinal key an upgraded
    // backend would use. Unreachable in practice — every store key is already
    // `property + ":" + extractStableLockValue(value)`, so a collision requires the
    // entry to collide with itself, i.e. a no-op self-assignment.
    if (withFallbacks[legacyKey] == null) {
      withFallbacks[legacyKey] = entry;
    }
  });
  return withFallbacks;
};
